import { storeToRefs } from 'pinia'
import { logService } from '@nsftx/seven-client-retail-utils'
import type { ClientTicket } from '#src/stores/tickets/types'
import type { IndexedDbDriver } from '#src/interfaces/IndexedDdDriver'
import { useTicketsStore } from '#src/stores/tickets/ticketsStore'
import errorParser from '#src/utils/errorParser'

interface UseLatestBetslipsOptions {
  retentionDays: number
}

const LOG_PREFIX = '[useLatestBetslips]'

export const useLatestBetslips = (
  slipsStorageDriver: IndexedDbDriver,
  options: UseLatestBetslipsOptions
) => {
  const { tickets } = storeToRefs(useTicketsStore())
  const { retentionDays } = options

  const deleteObsoleteTickets = async () => {
    try {
      const deletedTicketsCount = await slipsStorageDriver.removeAll(retentionDays)
      logService.debug(`${LOG_PREFIX} Tickets has been successfully deleted`, {
        code: 'SGP_BETSLIP_TICKETS_DELETE_SUCCESS',
        details: {
          count: deletedTicketsCount
        }
      })
    } catch (error) {
      logService.error(`${LOG_PREFIX} Failed to delete tickets`, {
        code: 'SGP_BETSLIP_TICKETS_DELETE_ERROR',
        ...errorParser.parseUpstream(error)
      })
    }
  }

  const syncStorageTicketsToStore = async () => {
    await deleteObsoleteTickets()

    const localTickets = await slipsStorageDriver.getAll()
    if (!localTickets.length) {
      return
    }

    const clientTickets = new Map<string, ClientTicket>()
    localTickets.forEach((ticket) => {
      if (!clientTickets.has(ticket.feBetslipId)) {
        clientTickets.set(ticket.feBetslipId, ticket)
      }
    })

    tickets.value = [...clientTickets.values()].sort(
      (left, right) => right.timestamp - left.timestamp
    )
  }

  return {
    syncStorageTicketsToStore
  }
}
